import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  PlayIcon,
  StopIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CogIcon,
  UserIcon,
  ComputerDesktopIcon,
  SparklesIcon,
  BoltIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  LinkIcon,
  EyeIcon,
  XMarkIcon,
  BeakerIcon,
  ShieldCheckIcon,
  CloudIcon,
  ChatBubbleLeftEllipsisIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon as CheckCircleIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  ClockIcon as ClockIconSolid,
  BoltIcon as BoltIconSolid
} from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { workflowsService } from '@/services/workflows';
import { Workflow, WorkflowExecution } from '@/types';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system' | 'log' | 'step' | 'error' | 'tool_call' | 'handoff' | 'agent_activated' | 'tool_result';
  content: string;
  timestamp: Date;
  agent?: string;
  step?: number;
  metadata?: Record<string, any>;
  tool_name?: string;
  tool_result?: any;
  success?: boolean;
}

interface WorkflowChatState {
  phase: 'setup' | 'collecting_inputs' | 'executing' | 'completed' | 'error';
  workflow: Workflow | null;
  execution: WorkflowExecution | null;
  inputFields: Array<{
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'select';
    required: boolean;
    options?: string[];
    value: string;
    placeholder?: string;
  }>;
  currentAgent?: string;
  totalSteps?: number;
  completedSteps?: number;
  executionTime?: number;
  agentTrace: Array<{
    agent: string;
    timestamp: Date;
    action: string;
    status: 'active' | 'completed' | 'error';
    duration?: number;
  }>;
}

const WorkflowChat: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getCurrentWorkspaceId } = useWorkspace();
  
  const [state, setState] = useState<WorkflowChatState>({
    phase: 'setup',
    workflow: null,
    execution: null,
    inputFields: [],
    currentAgent: undefined,
    totalSteps: 0,
    completedSteps: 0,
    executionTime: 0,
    agentTrace: []
  });
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const workspaceId = getCurrentWorkspaceId();

  // Fetch workflow data
  const { data: workflow, isLoading, error } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () => workflowsService.getWorkflow(workflowId!),
    enabled: !!workflowId
  });

  useEffect(() => {
    if (workflow) {
      setState(prev => ({ ...prev, workflow, totalSteps: workflow.steps.length }));
      initializeChat(workflow);
    }
  }, [workflow]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = (workflow: Workflow) => {
    const welcomeMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'system',
      content: `# 🎭 Welcome to ${workflow.name}

**Orchestrator:** ${workflow.orchestrator_name || 'Workflow Assistant'}  
**Steps:** ${workflow.steps.length} agents ready to process your request

This workflow will guide you through the following process:
${workflow.steps.map((step, i) => `${i + 1}. **Agent ${i + 1}** - Processing step`).join('\n')}

Let's collect the information needed to begin...`,
      timestamp: new Date()
    };

    // Determine input fields based on workflow pattern and context
    const inputFields = getRequiredInputFields(workflow);
    
    setMessages([welcomeMessage]);
    setState(prev => ({
      ...prev,
      phase: 'collecting_inputs',
      inputFields
    }));

    // Add input collection message with better formatting
    setTimeout(() => {
      const inputMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `## 🚀 Workflow Ready to Execute

${inputFields.length > 0 ? `To provide context for this workflow, you can optionally fill in:

${inputFields.map((field, i) => `**${i + 1}. ${field.label}** *(optional)*`).join('\n')}

### How to provide context:
- **Form Method:** Use the input form below and click "Start Workflow"
- **Natural Language:** Type your context in the chat (e.g., "Task ID is BIL-27, GitHub repo is mycompany/myproject, confluence space BIL")
- **Direct Execution:** Click "Start Workflow" without any fields - the orchestrator will ask for clarification if needed

All fields are optional - the agents have their own instructions and can work with minimal context.` : `This workflow is ready to execute immediately. The agents have comprehensive instructions and can work independently.

You can start the workflow directly or provide additional context if needed.`}

Ready when you are! 🚀`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, inputMessage]);
    }, 1000);
  };

  const getRequiredInputFields = (workflow: Workflow) => {
    // Dynamic input fields based on workflow type and agent tools
    const baseFields: Array<{
      name: string;
      label: string; 
      type: 'text' | 'textarea' | 'select';
      required: boolean;
      options?: string[];
      value: string;
      placeholder?: string;
    }> = [];

    // Check if workflow contains agents with Jira tools
    const hasJiraTools = workflow.steps?.some(step => {
      // For now, we'll check by pattern and naming since we don't have full agent tool data in the workflow object
      // In a real implementation, we'd fetch the actual agent details and check their tools
      return true; // Always show Jira field for now since most workflows use Jira
    });

    // Check workflow name AND pattern for SDLC workflows
    const isSDLCWorkflow = workflow.name.toLowerCase().includes('sdlc') || 
                          workflow.name.toLowerCase().includes('development') ||
                          workflow.pattern === 'sequential_handoff';

    // Check if it's an agent-as-tool workflow
    const isAgentAsToolWorkflow = workflow.pattern === 'agent-tool';
    
    // Add Jira Task ID for SDLC workflows, agent-as-tool workflows, or workflows with Jira tools
    if (isSDLCWorkflow || isAgentAsToolWorkflow || hasJiraTools) {
      baseFields.push({
        name: 'jira_task_id',
        label: 'Jira Task ID',
        type: 'text',
        required: false,
        options: [],
        value: '',
        placeholder: 'e.g., BIL-123'
      });
    }

    return baseFields;
  };

  const getMessageIcon = (message: ChatMessage) => {
    switch (message.type) {
      case 'user':
        return <UserIcon className="h-5 w-5" />;
      case 'assistant':
        return <SparklesIcon className="h-5 w-5" />;
      case 'system':
        return <CogIcon className="h-5 w-5" />;
      case 'tool_call':
        return <BoltIcon className="h-5 w-5" />;
      case 'handoff':
        return <ArrowPathIcon className="h-5 w-5" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'step':
        // Use different icons based on success/failure for tool results
        if (message.content.includes('❌')) {
          return <ExclamationTriangleIcon className="h-5 w-5" />;
        } else if (message.content.includes('✅')) {
          return <CheckCircleIcon className="h-5 w-5" />;
        }
        return <CheckCircleIcon className="h-5 w-5" />;
      default:
        return <ComputerDesktopIcon className="h-5 w-5" />;
    }
  };

  const getMessageStyle = (message: ChatMessage) => {
    // Special styling for tool results based on success/failure
    if (message.type === 'step' && message.tool_name) {
      const isSuccess = message.success !== false && 
        (message.content.includes('✅') || !message.content.includes('❌'));
      
      if (isSuccess) {
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-l-4 border-l-green-500 border border-green-200 dark:border-green-700/50 max-w-[85%] rounded-xl rounded-l-none px-4 py-3 shadow-sm',
          text: 'text-green-900 dark:text-green-100'
        };
      } else {
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border-l-4 border-l-red-500 border border-red-200 dark:border-red-700/50 max-w-[85%] rounded-xl rounded-l-none px-4 py-3 shadow-sm',
          text: 'text-red-900 dark:text-red-100'
        };
      }
    }

    switch (message.type) {
      case 'user':
        return {
          container: 'flex justify-end mb-4',
          bubble: 'bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white max-w-[80%] rounded-2xl rounded-br-md px-4 py-3 shadow-lg',
          text: 'text-white'
        };
      case 'assistant':
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 max-w-[80%] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm',
          text: 'text-gray-900 dark:text-gray-100'
        };
      case 'system':
        return {
          container: 'flex justify-center mb-4',
          bubble: 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-700/50 max-w-[90%] rounded-xl px-4 py-3 shadow-sm',
          text: 'text-purple-900 dark:text-purple-100'
        };
      case 'tool_call':
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-l-amber-500 border border-amber-200 dark:border-amber-700/50 max-w-[80%] rounded-xl rounded-l-none px-4 py-3 shadow-sm',
          text: 'text-amber-900 dark:text-amber-100'
        };
      case 'handoff':
        return {
          container: 'flex justify-center mb-4',
          bubble: 'bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-700/50 max-w-[80%] rounded-xl px-4 py-3 shadow-sm',
          text: 'text-teal-900 dark:text-teal-100'
        };
      case 'error':
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border border-red-200 dark:border-red-700/50 max-w-[80%] rounded-xl px-4 py-3 shadow-sm',
          text: 'text-red-900 dark:text-red-100'
        };
      case 'step':
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-700/50 max-w-[85%] rounded-xl px-4 py-3 shadow-sm',
          text: 'text-blue-900 dark:text-blue-100'
        };
      default:
        return {
          container: 'flex justify-start mb-4',
          bubble: 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 max-w-[80%] rounded-xl px-4 py-3 shadow-sm',
          text: 'text-gray-900 dark:text-gray-100'
        };
    }
  };

  // Helper function to detect if a tool result is successful
  const isToolResultSuccessful = (result: string): boolean => {
    try {
      // Handle Python-style True/False by converting to lowercase first
      const pythonToJsResult = result.replace(/True/g, 'true').replace(/False/g, 'false').replace(/None/g, 'null');
      
      // Try to parse as JSON
      const parsed = JSON.parse(pythonToJsResult);
      if (typeof parsed === 'object' && parsed !== null) {
        // Check for explicit success field (handle both boolean and string)
        if ('success' in parsed) {
          const successValue = parsed.success;
          if (typeof successValue === 'boolean') {
            return successValue;
          }
          if (typeof successValue === 'string') {
            return successValue.toLowerCase() === 'true';
          }
        }
        // Check for error field - if present, it's usually a failure
        if ('error' in parsed && parsed.error) {
          return false;
        }
        // Check for common success indicators
        if (parsed.task_id || parsed.page_id || (parsed.comment && parsed.comment.id)) {
          return true;
        }
        // If it's a structured object without explicit success/error, check for data
        return Object.keys(parsed).length > 0;
      }
    } catch (e) {
      // Not JSON, check for common error patterns in text
      const lowerResult = result.toLowerCase();
      if (lowerResult.includes('error') || lowerResult.includes('failed') || lowerResult.includes('exception')) {
        return false;
      }
    }
    
    // Default to successful if no clear error indicators
    return true;
  };

  // Helper function to format tool results nicely
  const formatToolResult = (result: string): string => {
    console.log('🎨 formatToolResult called with:', result.substring(0, 100) + '...');
    
    try {
      // Handle Python-style True/False and single quotes
      let pythonToJsResult = result
        .replace(/True/g, 'true')
        .replace(/False/g, 'false')
        .replace(/None/g, 'null')
        .replace(/'/g, '"'); // Convert single quotes to double quotes for valid JSON
        
      console.log('🎨 After Python conversion:', pythonToJsResult.substring(0, 100) + '...');
      
      const parsed = JSON.parse(pythonToJsResult);
      console.log('🎨 Parsed JSON successfully:', parsed);
      
      if (typeof parsed === 'object' && parsed !== null) {
        console.log('🎨 Object detected, checking conditions...');
        
        // Format Confluence page results (like your page creation example)
        if (parsed.success && parsed.page_id) {
          console.log('🎨 Confluence page result detected');
          return `**✅ Page Created Successfully**

📄 **Page Details:**
• **Page ID**: ${parsed.page_id}
• **Title**: ${parsed.title || 'New Page'}
• **Version**: ${parsed.version || 'N/A'}
• **URL**: ${parsed.url ? `[View Page](${parsed.url})` : 'N/A'}
• **Space**: ${parsed.space_key || 'Unknown'}`;
        }
        
        // Format Jira task update results (like your BIL-23 example)
        if (parsed.success && parsed.task_id && parsed.summary && !parsed.comment) {
          console.log('🎨 Jira task update detected');
          return `**✅ Task Updated Successfully**

🎯 **Task Details:**
• **Task ID**: [${parsed.task_id}](${parsed.url || '#'})
• **Summary**: ${parsed.summary}
${parsed.description ? `• **Description**: ${parsed.description.substring(0, 200)}${parsed.description.length > 200 ? '...' : ''}` : ''}
${parsed.id ? `• **Internal ID**: \`${parsed.id}\`` : ''}`;
        }

        // Format Jira comment results with full details
        if (parsed.success && parsed.comment && parsed.comment.id) {
          console.log('🎨 Jira comment result detected');
          return `**✅ Comment Added Successfully**

📝 **Comment Details:**
• **ID**: ${parsed.comment.id}
• **Author**: ${parsed.comment.author || 'Unknown'}
• **Created**: ${parsed.comment.created ? new Date(parsed.comment.created).toLocaleString() : 'N/A'}

📄 **Comment Content:**
"${parsed.comment.body ? parsed.comment.body.substring(0, 200) + (parsed.comment.body.length > 200 ? '...' : '') : 'Comment added successfully'}"

${parsed.task_id ? `🎯 **Linked Task**: [${parsed.task_id}](${parsed.url || '#'})` : ''}`;
        }
        
        // Format search results
        if (parsed.success && parsed.pages) {
          return `**✅ Search Completed**

📊 **Search Results:**
• **Pages Found**: ${parsed.pages.length}
• **Space**: ${parsed.space_key || 'Multiple spaces'}
• **Total Results**: ${parsed.total_found || parsed.pages.length}`;
        }
        
        // Format GitHub repository results
        if (parsed.success && (parsed.repository || parsed.repo_name)) {
          return `**✅ Repository Operation Successful**

🔗 **Repository Details:**
• **Name**: ${parsed.repository || parsed.repo_name}
• **Operation**: ${parsed.operation || 'Repository accessed'}
${parsed.url ? `• **URL**: [View Repository](${parsed.url})` : ''}`;
        }
        
        // Format generic success with better structure
        if (parsed.success) {
          const details = Object.entries(parsed)
            .filter(([key, value]) => key !== 'success' && value && typeof value !== 'object')
            .slice(0, 5)
            .map(([key, value]) => {
              const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              let displayValue = String(value);
              
              // Format URLs as links
              if (key.includes('url') || displayValue.startsWith('http')) {
                displayValue = `[${displayValue.length > 50 ? 'View Link' : displayValue}](${value})`;
              }
              // Format IDs and codes
              else if (key.includes('id') || key.includes('key')) {
                displayValue = `\`${value}\``;
              }
              // Truncate long values
              else if (displayValue.length > 100) {
                displayValue = displayValue.substring(0, 100) + '...';
              }
              
              return `• **${displayKey}**: ${displayValue}`;
            })
            .join('\n');
            
          return `**✅ Operation Completed Successfully**

📋 **Details:**
${details}`;
        }
        
        // Format errors
        if (parsed.error) {
          return `**❌ Error Occurred**

🚨 **Error Details:**
${parsed.error}

${parsed.details ? `**Additional Info:**\n${parsed.details}` : ''}`;
        }
        
        // Format as structured data for complex objects
        const simplifiedData = Object.entries(parsed)
          .filter(([key, value]) => typeof value !== 'object' || value === null)
          .slice(0, 8)
          .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {} as any);
          
        return `**📊 Structured Result:**

\`\`\`json
${JSON.stringify(simplifiedData, null, 2)}
\`\`\``;
      }
    } catch (e) {
      console.log('🎨 JSON parsing failed, attempting to handle incomplete JSON:', e.message);
      
      // Try to handle incomplete JSON by finding the last valid part
      try {
        // Handle Python-style conversions first
        let pythonToJsResult = result
          .replace(/True/g, 'true')
          .replace(/False/g, 'false')
          .replace(/None/g, 'null')
          .replace(/'/g, '"');
        
        // Try to find where the JSON becomes incomplete
        let validPart = pythonToJsResult;
        let lastValidIndex = pythonToJsResult.length;
        
        // Work backwards to find the last valid JSON structure
        for (let attempt = 0; attempt < 10; attempt++) {
          try {
            // Try different truncation points
            let testPart;
            if (attempt === 0) {
              // First try: find last complete property
              const lastCommaIndex = pythonToJsResult.lastIndexOf(',');
              const lastColonIndex = pythonToJsResult.lastIndexOf(':');
              
              if (lastCommaIndex > lastColonIndex) {
                // Cut at last comma
                testPart = pythonToJsResult.substring(0, lastCommaIndex);
              } else {
                // Cut at last complete property
                testPart = pythonToJsResult.substring(0, lastColonIndex);
                const lastQuoteIndex = testPart.lastIndexOf('"');
                if (lastQuoteIndex > 0) {
                  testPart = testPart.substring(0, lastQuoteIndex);
                }
              }
            } else {
              // Subsequent tries: reduce by chunks
              testPart = pythonToJsResult.substring(0, lastValidIndex - (attempt * 50));
            }
            
            if (testPart.length < 20) break; // Don't go too short
            
            // Try to close incomplete objects/arrays
            let closedPart = testPart;
            let openBraces = (testPart.match(/{/g) || []).length;
            let closeBraces = (testPart.match(/}/g) || []).length;
            let openBrackets = (testPart.match(/\[/g) || []).length;
            let closeBrackets = (testPart.match(/\]/g) || []).length;
            
            // Add missing closing braces/brackets
            for (let i = 0; i < openBraces - closeBraces; i++) {
              closedPart += '}';
            }
            for (let i = 0; i < openBrackets - closeBrackets; i++) {
              closedPart += ']';
            }
            
            const parsed = JSON.parse(closedPart);
            console.log('🎨 Successfully parsed truncated JSON on attempt', attempt + 1, ':', parsed);
            
            // If we have at least some success info, format it
            if (typeof parsed === 'object' && parsed !== null) {
              
              // Try specific formatting first
              if (parsed.success && parsed.page_id) {
                return `**✅ Page Created Successfully** ⚠️ **(Partial Result)**

📄 **Page Details:**
• **Page ID**: ${parsed.page_id}
• **Title**: ${parsed.title || 'New Page'}
• **Version**: ${parsed.version || 'N/A'}
• **URL**: ${parsed.url ? `[View Page](${parsed.url})` : 'N/A'}
• **Space**: ${parsed.space_key || 'Unknown'}

*Note: Result was truncated, showing available information.*`;
              }
              
              if (parsed.success && parsed.task_id && !parsed.comment) {
                return `**✅ Task Updated Successfully** ⚠️ **(Partial Result)**

🎯 **Task Details:**
• **Task ID**: [${parsed.task_id}](${parsed.url || '#'})
• **Summary**: ${parsed.summary || 'Task updated'}
${parsed.id ? `• **Internal ID**: \`${parsed.id}\`` : ''}

*Note: Result was truncated, showing available information.*`;
              }
              
              if (parsed.success && parsed.comment && parsed.comment.id) {
                return `**✅ Comment Added Successfully** ⚠️ **(Partial Result)**

📝 **Comment Details:**
• **ID**: ${parsed.comment.id}
• **Author**: ${parsed.comment.author || 'Unknown'}
${parsed.task_id ? `• **Linked Task**: [${parsed.task_id}](${parsed.url || '#'})` : ''}

*Note: Result was truncated, showing available information.*`;
              }
              
              // Generic success formatting
              if (parsed.success) {
                const details = Object.entries(parsed)
                  .filter(([key, value]) => key !== 'success' && value && typeof value !== 'object')
                  .slice(0, 5)
                  .map(([key, value]) => {
                    const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    let displayValue = String(value);
                    
                    if (key.includes('url') || displayValue.startsWith('http')) {
                      displayValue = `[${displayValue.length > 50 ? 'View Link' : displayValue}](${value})`;
                    } else if (key.includes('id') || key.includes('key')) {
                      displayValue = `\`${value}\``;
                    } else if (displayValue.length > 100) {
                      displayValue = displayValue.substring(0, 100) + '...';
                    }
                    
                    return `• **${displayKey}**: ${displayValue}`;
                  })
                  .join('\n');
                  
                return `**✅ Operation Completed Successfully** ⚠️ **(Partial Result)**

📋 **Available Details:**
${details}

*Note: Result was truncated, showing available information.*`;
              }
              
              // Error formatting
              if (parsed.error) {
                return `**❌ Error Occurred**

🚨 **Error Details:**
${parsed.error}

${parsed.details ? `**Additional Info:**\n${parsed.details}` : ''}`;
              }
            }
            
            break;
          } catch (innerE) {
            lastValidIndex = lastValidIndex - 50;
            if (lastValidIndex <= 50) break;
          }
        }
      } catch (parseError) {
        console.log('🎨 Failed to parse even partial JSON');
      }
      
      // If not JSON, check if it's already formatted nicely
      if (result.includes('✅') || result.includes('❌') || result.includes('**')) {
        return result;
      }
      
      // Return as code block for raw text
      return `\`\`\`
${result}
\`\`\``;
    }
    
    return result;
  };

  // Get a better tool name from the tool_name field
  const getDisplayToolName = (toolName?: string): string => {
    if (!toolName || toolName === 'Unknown Tool') {
      return 'Tool';
    }
    
    // Convert snake_case to Title Case
    return toolName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get tool icon based on tool name
  const getToolIcon = (toolName: string) => {
    const name = toolName.toLowerCase();
    if (name.includes('jira') || name.includes('task') || name.includes('issue')) {
      return BoltIcon;
    }
    if (name.includes('confluence') || name.includes('page') || name.includes('document')) {
      return DocumentTextIcon;
    }
    if (name.includes('github') || name.includes('git') || name.includes('repository') || name.includes('code')) {
      return CodeBracketIcon;
    }
    if (name.includes('search') || name.includes('query')) {
      return SparklesIcon;
    }
    if (name.includes('create') || name.includes('add') || name.includes('new')) {
      return PlusIcon;
    }
    if (name.includes('update') || name.includes('edit') || name.includes('modify')) {
      return PencilIcon;
    }
    if (name.includes('link') || name.includes('connect')) {
      return LinkIcon;
    }
    return WrenchScrewdriverIcon;
  };

  const handleStartWorkflow = async () => {
    if (!state.workflow) return;

    // Collect input data
    const inputData = state.inputFields.reduce((acc, field) => {
      if (field.type === 'select') {
        acc[field.name] = field.options?.find(opt => opt.toLowerCase() === field.value.toLowerCase()) || '';
      } else if (field.type === 'textarea') {
        acc[field.name] = field.value.trim();
      } else {
        acc[field.name] = field.value.trim();
      }
      return acc;
    }, {} as Record<string, string>);

    // Add natural language input if provided
    if (input.trim()) {
      inputData['additional_context'] = input.trim();
    }

    // No required fields anymore - all context is optional
    setState(prev => ({ ...prev, phase: 'executing' }));

    const startMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'system',
      content: `## 🚀 Starting Workflow Execution

**Input Data:**
${Object.entries(inputData).map(([key, value]) => `- **${key.replace('_', ' ')}:** ${value}`).join('\n')}

**Workflow:** ${state.workflow.name}
**Orchestrator:** ${state.workflow.orchestrator_name}

Initializing agents and beginning execution...`,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, startMessage]);
    setInput('');

    // Create context variables
    const contextVariables = {
      workspace_id: workspaceId,
      user_id: user?.id,
      workflow_id: state.workflow.id,
      ...inputData
    };

    try {
      // Start real streaming workflow execution
      await workflowsService.executeWorkflowStreaming(
        state.workflow.id.toString(),
        {
          input_data: JSON.stringify(inputData),
          context_variables: contextVariables,
          max_turns: 50
        },
        // onEvent handler - receives real-time events from agents
        (event) => {
          handleStreamEvent(event);
        },
        // onError handler
        (error) => {
          console.error('Streaming workflow execution failed:', error);
          
          const errorMessage: ChatMessage = {
            id: Date.now().toString(),
            type: 'error',
            content: `## ❌ Execution Failed

**Error:** ${error.message || 'Unknown error occurred'}

**Troubleshooting:**
- Check your network connection
- Verify all required fields are filled
- Try again or contact support if the issue persists

**Support:** If this problem continues, please report it with the execution details above.`,
            timestamp: new Date()
          };

          setMessages(prev => [...prev, errorMessage]);
          setState(prev => ({ ...prev, phase: 'error' }));
          toast.error('Workflow execution failed');
        },
        // onComplete handler
        () => {
          const completionMessage: ChatMessage = {
            id: Date.now().toString(),
            type: 'system',
            content: `## ✅ Workflow Execution Completed

All agents have finished processing successfully! 

**What happened:**
- All workflow steps were executed
- Agents coordinated and shared information
- Final results and artifacts have been generated

**Next steps:**
- Review the results and artifacts above
- Check any generated links or files
- Start a new workflow if needed

Thank you for using Orchestra! 🎉`,
            timestamp: new Date()
          };

          setMessages(prev => [...prev, completionMessage]);
          setState(prev => ({ ...prev, phase: 'completed' }));
          toast.success('Workflow completed successfully!');
        }
      );

    } catch (error: any) {
      console.error('Failed to start workflow execution:', error);
      
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'error',
        content: `## ❌ Failed to Start Execution

**Error:** ${error?.message || 'Unknown error occurred'}

**Possible causes:**
- Network connectivity issues
- Backend service unavailable
- Invalid workflow configuration

Please try again or contact support if the issue persists.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
      setState(prev => ({ ...prev, phase: 'error' }));
      toast.error('Failed to start workflow execution');
    }
  };

  // Handle real-time streaming events from the backend
  const handleStreamEvent = (event: any) => {
    console.log('Received stream event:', event);
    setIsTyping(false);

    switch (event.type) {
      case 'workflow_initialized':
        const initMessage: ChatMessage = {
          id: `init-${event.execution_id}`,
          type: 'system',
          content: `## 🔧 Workflow Initialized

**Orchestrator:** ${event.orchestrator}  
**Agent Pipeline:** ${event.agents.join(' → ')}  
**Execution ID:** \`${event.execution_id}\`

Agent coordination has begun. The orchestrator is now managing the workflow execution...`,
          timestamp: new Date(),
          metadata: event
        };
        setMessages(prev => [...prev, initMessage]);
        break;

      case 'agent_message':
        const agentMessage: ChatMessage = {
          id: `msg-${event.execution_id}-${event.step}-${Date.now()}`,
          type: 'assistant',
          content: event.content,
          timestamp: new Date(),
          agent: event.agent_name,
          step: event.step,
          metadata: event
        };
        setMessages(prev => [...prev, agentMessage]);
        break;

      case 'agent_handoff':
        const handoffMessage: ChatMessage = {
          id: `handoff-${event.execution_id}-${event.step}`,
          type: 'handoff',
          content: `## 🔄 Agent Handoff

**From:** ${event.from_agent} ✅  
**To:** ${event.to_agent} 🎯  
**Step:** ${event.step}

${event.to_agent} is now taking over the workflow execution...`,
          timestamp: new Date(),
          step: event.step,
          metadata: event
        };
        setMessages(prev => [...prev, handoffMessage]);
        setState(prev => ({ ...prev, currentAgent: event.to_agent }));
        break;

      case 'tool_call':
        const displayName = getDisplayToolName(event.tool_name);
        const toolCallMessage: ChatMessage = {
          id: `tool-${event.execution_id}-${event.step}-${Date.now()}`,
          type: 'step',
          content: `## 🔧 Tool Call

**Tool:** \`${displayName}\`  
**Agent:** ${event.agent_name}  
**Step:** ${event.step}

${event.tool_input ? `**Parameters:**\n\`\`\`json\n${typeof event.tool_input === 'object' ? JSON.stringify(event.tool_input, null, 2) : event.tool_input}\n\`\`\`` : ''}`,
          timestamp: new Date(),
          agent: event.agent_name,
          step: event.step,
          metadata: event,
          tool_name: displayName
        };
        setMessages(prev => [...prev, toolCallMessage]);
        break;

      case 'tool_result':
        console.log('🔧 Tool result event:', event);
        console.log('🔧 Event keys:', Object.keys(event));
        console.log('🔧 Event tool_name:', event.tool_name);
        console.log('🔧 Event result:', event.result);
        console.log('🔧 Event error:', event.error);
        
        const toolResult = event.result || event.error || 'Tool execution completed';
        const isSuccess = isToolResultSuccessful(toolResult);
        const formattedResult = formatToolResult(toolResult);
        
        // Try different ways to get the tool name from the event or recent messages
        let eventToolName = event.tool_name || event.tool || event.name;
        
        // If no tool name in event, try to find it from the most recent tool call with same step
        if (!eventToolName || eventToolName === 'Unknown Tool') {
          const recentToolCall = messages
            .slice()
            .reverse()
            .find(msg => msg.type === 'step' && msg.step === event.step && msg.tool_name && msg.content.includes('🔧 Tool Call'));
          
          if (recentToolCall && recentToolCall.tool_name) {
            eventToolName = recentToolCall.tool_name;
            console.log('🔧 Found tool name from recent tool call:', eventToolName);
          }
        }
        
        // Also try to find from recent tool call with same agent and close step number
        if (!eventToolName || eventToolName === 'Unknown Tool') {
          const recentToolCall = messages
            .slice()
            .reverse()
            .find(msg => 
              msg.type === 'step' && 
              msg.agent === event.agent_name && 
              msg.tool_name && 
              msg.content.includes('🔧 Tool Call') &&
              Math.abs((msg.step || 0) - (event.step || 0)) <= 2
            );
          
          if (recentToolCall && recentToolCall.tool_name) {
            eventToolName = recentToolCall.tool_name;
            console.log('🔧 Found tool name from recent agent tool call:', eventToolName);
          }
        }
        
        const displayToolName = getDisplayToolName(eventToolName || 'Unknown Tool');
        
        console.log('🔧 Raw event tool name:', eventToolName);
        console.log('🔧 Display tool name:', displayToolName);
        console.log('🔧 Is success:', isSuccess);
        console.log('🔧 Formatted result preview:', formattedResult.substring(0, 200));
        
        // Create the message content with better formatting (removed tool label)
        const messageContent = `## ${isSuccess ? '✅' : '❌'} Tool Result

**Status:** ${isSuccess ? '✅ Success' : '❌ Failed'}

${formattedResult}`;

        const resultMessage: ChatMessage = {
          id: `result-${event.execution_id}-${event.step}-${Date.now()}`,
          type: 'step',
          content: messageContent,
          timestamp: new Date(),
          step: event.step,
          metadata: event,
          tool_name: displayToolName,
          success: isSuccess
        };
        setMessages(prev => [...prev, resultMessage]);
        break;

      case 'user_input_required':
        const inputRequestMessage: ChatMessage = {
          id: `input-req-${event.execution_id}-${event.step}`,
          type: 'assistant',
          content: `## 🤔 Input Required

**${event.agent_name}** needs your input to continue:

${event.question}

*Please respond in the chat below to continue the workflow.*`,
          timestamp: new Date(),
          agent: event.agent_name,
          step: event.step,
          metadata: { ...event, requires_response: true }
        };
        setMessages(prev => [...prev, inputRequestMessage]);
        
        // Re-enable input for user response
        setState(prev => ({ ...prev, phase: 'collecting_inputs' }));
        break;

      case 'workflow_completed':
        const finalMessage: ChatMessage = {
          id: `final-${event.execution_id}`,
          type: 'system',
          content: `## 🎉 Workflow Completed Successfully

### Final Results:
${event.final_output}

### Execution Summary:
- **Total Steps:** ${event.steps_completed}
- **Execution ID:** \`${event.execution_id}\`
- **Duration:** ${event.duration || 'N/A'}

All agents have completed their tasks successfully! 🚀`,
          timestamp: new Date(),
          metadata: event
        };
        setMessages(prev => [...prev, finalMessage]);
        setState(prev => ({ 
          ...prev, 
          phase: 'completed',
          currentAgent: undefined, // Clear current agent on completion
          totalSteps: event.steps_completed,
          completedSteps: event.steps_completed,
          executionTime: event.duration
        }));
        break;

      case 'stream_error':
      case 'execution_failed':
        const errorMsg: ChatMessage = {
          id: `error-${event.execution_id}-${Date.now()}`,
          type: 'error',
          content: `## ❌ Execution Error

**Error:** ${event.error}  
**Step:** ${event.step || 'Unknown'}  
**Execution ID:** \`${event.execution_id}\`

The workflow execution encountered an error and has been stopped.`,
          timestamp: new Date(),
          metadata: event
        };
        setMessages(prev => [...prev, errorMsg]);
        setState(prev => ({ ...prev, phase: 'error' }));
        break;

      default:
        // Handle unknown event types
        const unknownMessage: ChatMessage = {
          id: `unknown-${Date.now()}`,
          type: 'log',
          content: `📡 **Stream Event:** ${event.type}`,
          timestamp: new Date(),
          metadata: event
        };
        setMessages(prev => [...prev, unknownMessage]);
        break;
    }

    // Auto-scroll to bottom for new messages
    setTimeout(scrollToBottom, 100);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input.trim();
    setInput('');

    // If in input collection phase, try to parse the input
    if (state.phase === 'collecting_inputs') {
      handleNaturalLanguageInput(userInput);
    }

    // If workflow is executing and requires user response
    if (state.phase === 'executing' && state.execution) {
      try {
        await workflowsService.sendUserResponse(state.execution.id, userInput);
        
        // Show typing indicator
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 2000);
      } catch (error) {
        console.error('Failed to send user response:', error);
        toast.error('Failed to send response');
      }
    }
  };

  const handleNaturalLanguageInput = (userInput: string) => {
    // Simple parsing logic - in a real app, you'd use NLP
    const updates = { ...state };
    
    // Try to extract common patterns
    const patterns = [
      { regex: /task\s+(?:id|ID)[\s:]+([A-Z]+-\d+)/i, field: 'jira_task_id' },
      { regex: /jira[\s:]+([A-Z]+-\d+)/i, field: 'jira_task_id' },
      { regex: /repo(?:sitory)?[\s:]+([^\s]+)/i, field: 'github_repo' },
      { regex: /github[\s:]+([^\s]+)/i, field: 'github_repo' },
      { regex: /confluence[\s:]+(?:space[\s:]+)?([A-Z]+)/i, field: 'confluence_space' },
      { regex: /space[\s:]+(?:key[\s:]+)?([A-Z]+)/i, field: 'confluence_space' },
    ];

    patterns.forEach(({ regex, field }) => {
      const match = userInput.match(regex);
      if (match) {
        const fieldIndex = updates.inputFields.findIndex(f => f.name === field);
        if (fieldIndex !== -1) {
          updates.inputFields[fieldIndex].value = match[1];
        }
      }
    });

    // Set the main description if no specific field was found
    const descriptionField = updates.inputFields.find(f => f.name === 'task_description');
    if (descriptionField && !descriptionField.value) {
      descriptionField.value = userInput;
    }

    setState(prev => ({ ...prev, inputFields: updates.inputFields }));
    
    // Show a helpful message
    const helpMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'assistant',
      content: `Got it! I've extracted the following information from your message:

${Object.entries(updates.inputFields)
  .filter(([_, field]) => field.value)
  .map(([_, field]) => `- **${field.label}:** ${field.value}`)
  .join('\n')}

Feel free to add more details or click "Start Workflow" when ready! 🚀`,
      timestamp: new Date()
    };

    setTimeout(() => {
      setMessages(prev => [...prev, helpMessage]);
    }, 500);
  };

  const handleInputFieldChange = (fieldName: string, value: string) => {
    setState(prev => ({
      ...prev,
      inputFields: prev.inputFields.map(field =>
        field.name === fieldName ? { ...field, value } : field
      )
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Failed to load workflow</p>
          <button
            onClick={() => navigate('/workflows')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col overflow-hidden">
      {/* Fixed Header */}
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                if (state.phase === 'executing') {
                  toast.error('Cannot navigate away while workflow is executing');
                  return;
                }
                navigate('/workflows');
              }}
              disabled={state.phase === 'executing'}
              className={`p-2 rounded-lg transition-colors ${
                state.phase === 'executing'
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={state.phase === 'executing' ? 'Cannot navigate during execution' : 'Back to workflows'}
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3">
              {/* Orchestra Logo */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <SparklesIcon className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-white">Orchestra</span>
              </div>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {state.workflow?.name || 'Workflow Chat'}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {state.workflow?.description || 'Interactive workflow execution'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Enhanced Progress indicator */}
          {state.phase === 'executing' && (
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {state.currentAgent && (
                      <span className="text-blue-600 dark:text-blue-400">{state.currentAgent}</span>
                    )}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Step {state.completedSteps || 0} of {state.totalSteps || '?'}
                </div>
              </div>
              <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${(state.completedSteps || 0) / (state.totalSteps || 1) * 100}%` }}
                ></div>
              </div>
              {/* Execution lock indicator */}
              <div className="flex items-center space-x-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                <ClockIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">Executing</span>
              </div>
            </div>
          )}

          {/* Status indicator for completed/error states */}
          {state.phase === 'completed' && (
            <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
              <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Completed</span>
            </div>
          )}

          {state.phase === 'error' && (
            <div className="flex items-center space-x-2 px-3 py-1 bg-red-100 dark:bg-red-900/30 rounded-full">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-700 dark:text-red-300">Error</span>
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages - Scrollable Area */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-4xl mx-auto space-y-1">
          {messages.map((message) => {
            const style = getMessageStyle(message);
            return (
              <div key={message.id} className={style.container}>
                <div className={style.bubble}>
                  {/* Message header for non-user messages */}
                  {message.type !== 'user' && (
                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-600/50">
                      <div className="flex items-center space-x-2">
                        <div className="p-1 bg-gray-100 dark:bg-gray-700/70 rounded-md">
                          {getMessageIcon(message)}
                        </div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {message.agent || message.type.charAt(0).toUpperCase() + message.type.slice(1)}
                        </span>
                        {message.step && (
                          <span className="text-xs bg-gray-200 dark:bg-gray-600/70 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full font-medium">
                            Step {message.step}
                          </span>
                        )}
                      </div>
                      <span className="text-xs opacity-75 text-gray-500 dark:text-gray-400">
                        {message.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  )}

                  {/* Message content with markdown support */}
                  <div className={`prose prose-sm max-w-none ${style.text}`}>
                    <ReactMarkdown
                      components={{
                        code: ({ className, children, ...props }) => {
                          const match = /language-(\w+)/.exec(className || '');
                          const isCodeBlock = match;
                          return isCodeBlock ? (
                            <pre className={`p-3 rounded-md text-sm overflow-x-auto ${
                              message.type === 'user'
                                ? 'bg-blue-600 text-blue-100'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                            }`}>
                              <code className={className} {...props}>
                                {String(children).replace(/\n$/, '')}
                              </code>
                            </pre>
                          ) : (
                            <code className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                              message.type === 'user' 
                                ? 'bg-blue-600 text-blue-100' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`} {...props}>
                              {children}
                            </code>
                          );
                        },
                        h1: ({ children }) => (
                          <h1 className="text-lg font-bold mb-2 text-current">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-base font-semibold mb-2 mt-4 text-current">{children}</h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-sm font-medium mb-1 mt-3 text-current">{children}</h3>
                        ),
                        p: ({ children }) => (
                          <p className="mb-2 last:mb-0 text-current leading-relaxed">{children}</p>
                        ),
                        ul: ({ children }) => (
                          <ul className="space-y-1 my-2 text-current">{children}</ul>
                        ),
                        li: ({ children }) => (
                          <li className="flex items-start space-x-2 text-current">
                            <div className="w-1 h-1 rounded-full bg-current mt-2 flex-shrink-0 opacity-60"></div>
                            <div className="flex-1">{children}</div>
                          </li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-current">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-current opacity-90">{children}</em>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-current pl-4 italic opacity-80 text-current">
                            {children}
                          </blockquote>
                        ),
                        a: ({ children, href }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                          >
                            {children}
                            <LinkIcon className="h-3 w-3" />
                          </a>
                        )
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>

                  {/* User message timestamp */}
                  {message.type === 'user' && (
                    <div className="text-xs opacity-75 mt-2 text-right">
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex justify-start mb-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 max-w-[80%] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Agent is working...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Form (during collection phase) */}
      {state.phase === 'collecting_inputs' && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Workflow Configuration
              </h3>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                {isCollapsed ? (
                  <ChevronDownIcon className="h-5 w-5" />
                ) : (
                  <ChevronUpIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {!isCollapsed && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {state.inputFields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={field.value}
                        onChange={(e) => handleInputFieldChange(field.name, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select an option</option>
                        {field.options?.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        value={field.value}
                        onChange={(e) => handleInputFieldChange(field.name, e.target.value)}
                        placeholder={field.type === 'textarea' ? 'Type your response...' : field.placeholder}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center space-x-4">
              <button
                onClick={handleStartWorkflow}
                disabled={state.phase === 'executing'}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <PlayIcon className="h-4 w-4" />
                <span>Start Workflow</span>
              </button>
              
              <div className="text-sm text-gray-500 dark:text-gray-400">
                or continue typing below to add more context
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Input */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSendMessage} className="flex space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={state.phase === 'executing'}
                placeholder={
                  state.phase === 'executing'
                    ? 'Workflow is running... You can respond if agents ask questions'
                    : state.phase === 'collecting_inputs'
                    ? 'Type additional context or use the form above...'
                    : 'Type your message...'
                }
                className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              />
              <button
                type="submit"
                disabled={!input.trim() || (state.phase === 'executing' && !state.execution)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-blue-600 hover:text-blue-700 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkflowChat; 